import React, { useEffect, useRef, useState } from 'react';
import { Alert, Card, CardBody, CardHeader, Collapse, Progress } from 'reactstrap';

import { MAX_UPLOAD_SIZE_BYTES } from '~constants/common';

import { Button } from '~components';
import FileUpload from '~components/FileUpload';
import Icon, { faChevronDown, faChevronUp, faCircleCheck, faCircleExclamation, faRefresh } from '~components/icon';
import { Column, TableView } from '~components/table-view-v3';

import { useAcoUploadMutation } from '~support-at-home/actions/acoBudgets';

interface ComponentProps {
  className?: string;
}

interface BudgetError {
  id: number;
  line: number;
  reason: string;
}

const errorColumns: Column[] = [
  {
    id: 'line',
    header: 'Line',
    type: 'text',
  },
  {
    id: 'reason',
    header: 'Error Reason',
    type: 'text',
  },
];

const listItems = ['CSV format only', 'Maximum file size: 10 MB', 'Must contain valid ACO budget data'];

const UploadMultiple: React.FC<ComponentProps> = ({ className }) => {
  const [acoUpload, mutationResult] = useAcoUploadMutation();
  const { data, isError, error, isSuccess, reset } = mutationResult;

  const [uploadProgress, setUploadProgress] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<BudgetError[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [isErrorsOpen, setIsErrorsOpen] = useState(false);

  const hasTriggeredUpload = useRef(false);

  useEffect(() => {
    if (!file || hasTriggeredUpload.current) return;

    setUploadProgress(0);
    setApiError(null);
    setValidationErrors([]);
    hasTriggeredUpload.current = true;

    const formData = new FormData();
    formData.append('file', file);

    acoUpload({ formData, onProgress: setUploadProgress });
  }, [file, acoUpload]);

  useEffect(() => {
    if (isError && error) {
      const errorData = error as any;
      const message = errorData?.data?.message || errorData?.message || 'An unexpected error occurred';
      setApiError(message);

      // Extract validation errors if present and add id field for TableView
      const errors = errorData?.data?.errors || [];
      const errorsWithIds = errors.map((err: BudgetError, idx: number) => ({
        ...err,
        id: idx + 1,
      }));
      setValidationErrors(errorsWithIds);

      setUploadProgress(0);
      setFile(null);
      hasTriggeredUpload.current = false;
    }
  }, [isError, error]);

  const isUploading = !!file && uploadProgress < 100 && !data && !apiError;
  const isProcessing = uploadProgress === 100 && !data && !apiError;
  const isComplete = isSuccess && !!data && !apiError;
  const showUpload = !isUploading && !isProcessing && !isComplete;

  const handleFileChange = (chosen: File) => {
    // Enforce 10 MB max size client-side
    if (chosen.size > MAX_UPLOAD_SIZE_BYTES) {
      setApiError('Selected file exceeds the 10 MB limit. Please choose a smaller file.');
      setFile(null);
      hasTriggeredUpload.current = false;
      return;
    }

    // Clear any previous errors and accept file
    setApiError(null);
    setFile(chosen);
  };

  const handleUploadAnother = () => {
    setFile(null);
    setUploadProgress(0);
    setApiError(null);
    setValidationErrors([]);
    setIsErrorsOpen(true);
    reset();
    hasTriggeredUpload.current = false;
  };

  const renderProgressBar = () => {
    if (isUploading) {
      return <Progress value={uploadProgress} style={{ height: '25px' }} color="success" />;
    }
    if (isProcessing) {
      return <Progress value={100} style={{ height: '25px' }} color="success" animated striped />;
    }
    return null;
  };

  return (
    <div className={className}>
      {apiError && (
        <Alert color="danger" className="mb-2 p-2 w-100">
          <b>Upload Failed</b>
          <div className="mt-2 mb-0">{apiError}</div>
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Card className="rounded-0 mb-3">
          <CardHeader
            className="d-flex justify-content-between border-0 fw-bold align-items-center"
            type="button"
            onClick={() => setIsErrorsOpen(!isErrorsOpen)}
          >
            <div>
              <Icon icon={faCircleExclamation} className="text-danger me-2" />
              Validation Errors ({validationErrors.length})
            </div>
            <Icon icon={isErrorsOpen ? faChevronUp : faChevronDown} className="ms-2" />
          </CardHeader>
          <Collapse isOpen={isErrorsOpen}>
            <CardBody className="p-0">
              <TableView
                id="sah-budget-upload-errors"
                columns={errorColumns}
                data={validationErrors}
                emptyMessage="No errors to display"
                readonly
                striped
              />
            </CardBody>
          </Collapse>
        </Card>
      )}

      {showUpload && (
        <FileUpload
          allowedExtensions={['.csv']}
          allowedMimeTypes={['text/csv', 'application/vnd.ms-excel']}
          onChange={handleFileChange}
          label="Upload ACO Budget CSV"
          description="Drag & drop CSV file here, or click to browse"
          className="w-100"
          existingFileName={file?.name ?? null}
        />
      )}

      {(isUploading || isProcessing) && (
        <div className="mt-3">
          <div className="d-flex justify-content-between mb-1">
            <div className="text-muted">{isUploading ? 'Uploading…' : 'Processing…'}</div>
            <div>{isUploading && `${uploadProgress}%`}</div>
          </div>
          {renderProgressBar()}
        </div>
      )}

      {isComplete && (
        <Alert color="success" className="my-2 p-2 w-100">
          <Icon icon={faCircleCheck} className="me-2" />
          <b>File Uploaded Successfully</b>
          <div className="my-2">Your file has been imported. You can upload another.</div>
          <div className="d-flex justify-content-center">
            <Button
              id="sah-client-budgets-add-button"
              size="sm"
              color="success"
              className="align-center"
              onClick={handleUploadAnother}
            >
              <Icon className="me-2" icon={faRefresh} />
              Upload Another
            </Button>
          </div>
        </Alert>
      )}

      <div className="criteria-box bg-light p-2 rounded small mt-3">
        <div className="fw-semibold mb-2">File Requirements:</div>
        <ul className="mb-0 ps-3 text-muted">
          {listItems.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default UploadMultiple;
